import { useState, useEffect, useRef } from 'react';
import { FileText, Edit2, Check, X } from 'lucide-react';

export default function GoalNotes({ goal, onUpdateNotes, isAdmin }) {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(goal.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef(null);

  // Sync notes when goal changes
  useEffect(() => {
    setNotes(goal.notes || '');
  }, [goal.notes, goal.id]);

  // Focus textarea when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdateNotes(notes);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(goal.notes || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    // Cmd/Ctrl + Enter to save
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    // Escape to cancel
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-border">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <FileText size={16} className="text-monthly" />
          Notes
        </h3>

        {isAdmin && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] text-text-secondary
                       hover:text-text-primary hover:bg-surface-hover rounded-md transition-colors"
          >
            <Edit2 size={12} />
            Edit
          </button>
        )}

        {isEditing && (
          <div className="flex gap-1">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-text-secondary
                         hover:text-text-primary hover:bg-surface-hover rounded-md transition-colors"
            >
              <X size={12} />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-white
                         bg-accent hover:bg-accent/90 rounded-md transition-colors disabled:opacity-50"
            >
              <Check size={12} />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Notes content */}
      {isEditing ? (
        <div>
          <textarea
            ref={textareaRef}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add notes about this goal... (Markdown supported)"
            className="w-full min-h-[150px] p-3 text-sm text-text-primary bg-surface-elevated
                       border border-border rounded-lg resize-y outline-none
                       focus:border-accent transition-colors"
          />
          <p className="text-[10px] text-text-muted mt-1">
            Press Cmd/Ctrl + Enter to save, Escape to cancel
          </p>
        </div>
      ) : (
        <div
          onClick={() => isAdmin && setIsEditing(true)}
          className={`min-h-[60px] p-3 bg-surface-elevated rounded-lg border border-border/50
                      ${isAdmin ? 'cursor-pointer hover:border-border transition-colors' : ''}`}
        >
          {notes ? (
            <div className="text-sm text-text-secondary whitespace-pre-wrap">
              {notes}
            </div>
          ) : (
            <p className="text-sm text-text-muted italic">
              {isAdmin ? 'Click to add notes...' : 'No notes yet'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
